import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSettings } from "../../../contexts/settings-context";
import {
  PhoneIcon,
  SignalIcon,
  WifiIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import {
  fetchPhoneroPresenceCodes,
  fetchPhoneroUserPresenceState,
  updatePhoneroUserPresence,
} from "../../../api/company";
import { toast } from "react-hot-toast";

export default function CurrentPhoneSection(props) {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const { settings } = useSettings();
  const { language } = settings;

  const [phoneInfo, setPhoneInfo] = useState({
    connected: false,
    error: null,
  });
  const [presenceCodes, setPresenceCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [syncStatus, setSyncStatus] = useState({
    connected: false,
    message: "",
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Helper function to extract last 8 digits from phone number
  const extractLast8Digits = (phoneNumber) => {
    if (!phoneNumber) return null;
    const digitsOnly = phoneNumber.toString().replace(/\D/g, "");
    return digitsOnly.length >= 8 ? digitsOnly.slice(-8) : null;
  };

  // Helper function to get presence code description
  const getPresenceCodeDescription = (presenceId) => {
    const code = presenceCodes.find((c) => c.id === presenceId);
    return code ? t(code.description) : t("unknown");
  };

  // Fetch phone information and presence states
  useEffect(() => {
    async function fetchPhoneData() {
      if (!currentUser) return;

      setIsLoading(true);
      try {
        // Always try to fetch the current user's presence state
        const presenceResponse = await fetchPhoneroUserPresenceState();

        // Also fetch presence codes for the change status functionality
        const codesResponse = await fetchPhoneroPresenceCodes();

        if (codesResponse.data?.success) {
          setPresenceCodes(codesResponse.data.codes || []);
        }

        if (presenceResponse.data?.success) {
          const presence = presenceResponse.data.presence?.result[0];

          // Update sync status based on successful response
          setSyncStatus({
            connected: true,
            message: presence
              ? `${t("connected.to.phonero")} (${presence.presenceDescription})`
              : t("connected.to.phonero"),
          });

          if (presence) {
            setPhoneInfo({
              personId: presence.personId,
              username: presence.username,
              presenceId: presence.presenceId,
              presenceDescription: presence.presenceDescription,
              presenceText: presence.presenceText,
              mobile: presence.mobile,
              connected: true,
            });
          } else {
            setPhoneInfo({
              // mobile: mobileNumber || t("connected.to.phonero"),
              connected: true,
              error: t("presence.not.available"),
            });
          }
        } else {
          // Backend returned an error (user not connected, no phonero config, etc.)
          setSyncStatus({
            connected: false,
            message:
              presenceResponse.data?.message || t("not.connected.to.phonero"),
          });

          setPhoneInfo({
            error:
              presenceResponse.data?.message || t("presence.failed.to.load"),
            // mobile: mobileNumber,
            connected: false,
          });
        }
      } catch (error) {
        console.error("Phone data fetch error:", error);
        setPhoneInfo({
          error: t("presence.failed.to.load"),
          // mobile: mobileNumber,
          connected: false,
        });
      } finally {
        setIsLoading(false);
      }
    }

    if (currentUser) {
      fetchPhoneData();
    }
  }, [currentUser, t]);

  const handlePresenceUpdate = async (newPresenceId) => {
    if (!phoneInfo.personId && !phoneInfo.username) {
      toast.error(t("presence.cannot.update"));
      return;
    }

    setIsUpdating(true);
    try {
      const identifier = phoneInfo.personId || phoneInfo.username;
      const response = await updatePhoneroUserPresence(identifier, {
        presence_id: newPresenceId,
        presence_text: "",
        presence_expiry: null,
      });

      if (response.data?.success) {
        toast.success(t("presence.updated.successfully"));

        // Refresh phone data using the direct API
        const presenceResponse = await fetchPhoneroUserPresenceState();
        if (presenceResponse.data?.success) {
          const presence = presenceResponse.data.presence?.result[0];
          const mobileNumber = currentUser?.mobile_work || currentUser?.mobile;

          // Update sync status
          setSyncStatus({
            connected: true,
            message: presence
              ? `${t("connected.to.phonero")} (${presence.presenceDescription})`
              : t("connected.to.phonero"),
          });

          if (presence) {
            setPhoneInfo({
              personId: presence.personId,
              username: presence.username,
              presenceId: presence.presenceId,
              presenceDescription: presence.presenceDescription,
              presenceText: presence.presenceText,
              mobile: presence.mobile,
              connected: true,
            });
          }
        }
      } else {
        toast.error(t("presence.failed.to.update"));
      }
    } catch (error) {
      console.error("Presence update error:", error);
      toast.error(t("presence.failed.to.update"));
    } finally {
      setIsUpdating(false);
    }
  };

  const getPresenceStatusColor = (presenceId) => {
    switch (presenceId) {
      case 1:
        return "text-green-600";
      case 2:
        return "text-yellow-600";
      case 3:
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getPresenceStatusIcon = (presenceId) => {
    switch (presenceId) {
      case 1:
        return <SignalIcon className="h-5 w-5 text-green-600" />;
      case 2:
        return <SignalIcon className="h-5 w-5 text-yellow-600" />;
      case 3:
        return <SignalIcon className="h-5 w-5 text-red-600" />;
      default:
        return <SignalIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  // --- Render ---
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Main Button */}
      <div
        className="flex items-center justify-between p-4 min-h-[56px] cursor-pointer hover:bg-gray-50 transition-colors duration-200"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? t("Collapse") : t("Expand")}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`p-2 rounded-lg ${
              phoneInfo.connected ? "bg-green-50" : "bg-gray-50"
            }`}
          >
            <PhoneIcon
              className={`h-6 w-6 ${
                phoneInfo.connected ? "text-green-600" : "text-gray-400"
              }`}
            />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">
              {phoneInfo.connected && phoneInfo.presenceDescription
                ? t(
                    phoneInfo.presenceDescription?.charAt(0).toUpperCase() +
                      phoneInfo.presenceDescription?.slice(1).toLowerCase()
                  )
                : phoneInfo.error && !phoneInfo.connected
                ? phoneInfo.error
                : t("Phone Status")}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {phoneInfo.mobile ||
                extractLast8Digits(phoneInfo.username) ||
                currentUser?.mobile_work ||
                currentUser?.mobile ||
                t("no.number")}
            </p>
          </div>
        </div>
        <div className="p-2 rounded-lg">
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              {t("Phone Status & Actions")}
            </h4>

            {/* Connection Status */}
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200">
              <div
                className={`p-2 rounded-lg ${
                  syncStatus.connected ? "bg-green-50" : "bg-red-50"
                }`}
              >
                {syncStatus.connected ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                ) : (
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  {syncStatus.connected ? t("Connected") : t("Not Connected")}
                </div>
                <div className="text-sm text-gray-500">
                  {syncStatus.message}
                </div>
              </div>
            </div>

            {/* Current Status */}
            {phoneInfo.connected && phoneInfo.presenceId && (
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <div className="text-sm text-gray-500 mb-2">
                  {t("Current Status")}
                </div>
                <div className="flex items-center gap-2">
                  {getPresenceStatusIcon(phoneInfo.presenceId)}
                  <span
                    className={`font-medium ${getPresenceStatusColor(
                      phoneInfo.presenceId
                    )}`}
                  >
                    {t(
                      phoneInfo.presenceDescription?.charAt(0).toUpperCase() +
                        phoneInfo.presenceDescription?.slice(1).toLowerCase()
                    ) || t("presence.unknown")}
                  </span>
                </div>
                {phoneInfo.presenceText && (
                  <div className="text-sm text-gray-600 mt-1">
                    {t(phoneInfo.presenceText)}
                  </div>
                )}
              </div>
            )}

            {/* Status Change Buttons */}
            {phoneInfo.connected &&
              presenceCodes.length > 0 &&
              phoneInfo.presenceId && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    {t("Change Status")}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {presenceCodes.map((code) => (
                      <button
                        key={code.id}
                        onClick={() => handlePresenceUpdate(code.id)}
                        disabled={
                          isUpdating || code.id === phoneInfo.presenceId
                        }
                        className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                          code.id === phoneInfo.presenceId
                            ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                        } ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {t(code.description)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {/* Error States */}
            {!phoneInfo.connected && phoneInfo.error && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
                  <div className="text-sm text-red-800">{phoneInfo.error}</div>
                </div>
              </div>
            )}

            {phoneInfo.connected &&
              phoneInfo.error === "Presence status not available" && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2">
                    <SignalIcon className="h-5 w-5 text-yellow-600" />
                    <div className="text-sm text-yellow-800">
                      {t("connected.to.phonero.but.presence.not.available")}
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
